/**
 * 编辑态 `getYaml` 响应 → 向导各 Step 的 patch 组装。
 * 纯函数，由 `useModelAgentCreateWizard.applyYamlRetrieveDetail` 统一 setState。
 */
import type { IYamlConfigRetrieveDetail } from '@/pages/datalab/pages/Number/ModelManagementCenter/types';

import type {
  TStepFieldConfigPatch,
  TStepSelectTablesPatch,
  TStepYamlDraftPatch,
} from '../types';
import { buildEmptyCustomRowEdit } from './fieldConfigRowUtils';
import { buildFieldConfigLoadKey } from './wizardHelpers';
import { cubeConfigPatch } from './mapFieldConfigFromApi';

/** `buildApplyYamlRetrievePatches` 返回值，供 hook 按字段 apply */
export interface IApplyYamlRetrievePatches {
  yamlDraft: TStepYamlDraftPatch;
  fieldConfig: TStepFieldConfigPatch;
  selectTables?: TStepSelectTablesPatch;
  /** 有库表信息时写入，与 Step1 下一步缓存口径一致 */
  fieldConfigLoadKey?: string;
}

/**
 * 将 retrieve 详情转为各 Step patch。
 * @param routeUuid - 路由 query.uuid，detail.uuid 缺失时作 savedConfigUuid 回退
 */
export const buildApplyYamlRetrievePatches = (
  detail: IYamlConfigRetrieveDetail,
  routeUuid: string,
  gameCode: string,
): IApplyYamlRetrievePatches => {
  let fieldConfigPatch: TStepFieldConfigPatch = {};
  try {
    const cubeConfigJson = JSON.parse(
      detail.cube_config_json || '{}',
    ) as Record<string, unknown>;
    fieldConfigPatch = cubeConfigPatch(
      cubeConfigJson,
      detail.hydrated_field_config?.tables,
    );
  } catch {
    // cube_config_json 非法时仅恢复 YAML，Step2 由用户重新拉字段
    fieldConfigPatch = {};
  }

  const result: IApplyYamlRetrievePatches = {
    yamlDraft: {
      cube_name: detail.name?.trim() || '',
      yamlReady: Boolean(detail.yaml_context?.trim()),
      yamlText: detail.yaml_context ?? '',
      savedConfigUuid: detail.uuid ?? routeUuid,
      yamlLoading: false,
      yamlError: false,
    },
    fieldConfig: {
      ...fieldConfigPatch,
      dimInlineOpen: false,
      measInlineOpen: false,
      dimAddDraft: buildEmptyCustomRowEdit('dimension'),
      measAddDraft: buildEmptyCustomRowEdit('metric'),
    },
  };

  if (detail.database_uuid && detail.table_id) {
    const tableId = String(detail.table_id);
    const databaseUuid = detail.database_uuid;
    // 与 Step1 初始态、buildFieldConfigLoadKey 口径一致，避免 retrieve 无 schema 时 loadKey 与 state 不一致
    const schemaValue = detail.schema ?? '';
    result.selectTables = {
      databaseValue: databaseUuid,
      schemaValue,
      tablesValue: [tableId],
      tablesName: detail.table_name || tableId,
    };
    result.fieldConfigLoadKey = buildFieldConfigLoadKey(gameCode, {
      databaseValue: databaseUuid,
      schemaValue,
      tablesValue: [tableId],
    });
  }

  return result;
};
